import { useCallback, useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { InfoTooltip } from '@/ui-kit/Tips/InfoTooltip';

import { IPostUser } from '@/features/user/store/user.model';

type TSanctionEmbargoSummary = Pick<
  IPostUser,
  | 'entitySanctionsQuestionnaire'
  | 'activityOutsideEu'
  | 'economicSanctions'
  | 'residentCountriesSanctions'
  | 'involvedSanctions'
>;

export function SanctionEmbargoSummary({
  entitySanctionsQuestionnaire,
  activityOutsideEu,
  economicSanctions,
  residentCountriesSanctions,
  involvedSanctions,
}: TSanctionEmbargoSummary) {
  const { t } = useTranslation();

  const getBooleanValue = useCallback(
    (item) =>
      Number(item || 0) === 1
        ? t('translation:components.molecules.forms.yes')
        : t('translation:components.molecules.forms.no'),
    [t],
  );

  const rowData = useMemo(
    () => [
      {
        title: t('pages.dashboard.createUser.form.sanctionEmbargoQuestionnaire.entitySanctionTitle'),
        tooltip: t('pages.dashboard.createUser.form.sanctionEmbargoQuestionnaire.entitySanctionTooltip'),
        value: entitySanctionsQuestionnaire
          ? t(`pages.dashboard.createUser.form.entitySanctionsQuestions.${entitySanctionsQuestionnaire}`)
          : '-',
      },
      {
        title: t('pages.dashboard.createUser.form.commercialActivityEU'),
        tooltip: t('pages.dashboard.createUser.form.sanctionEmbargoQuestionnaire.activityOutsideEu'),
        value: getBooleanValue(activityOutsideEu),
      },
      ...(Number(activityOutsideEu || 0) === 1
        ? [
            {
              title: t('pages.dashboard.createUser.form.economicSanctions'),
              tooltip: t('pages.dashboard.createUser.form.sanctionEmbargoQuestionnaire.economicSanctions'),
              value: getBooleanValue(economicSanctions),
            },
            {
              title: t('pages.dashboard.createUser.form.residentCountriesSanctions'),
              tooltip: t('pages.dashboard.createUser.form.sanctionEmbargoQuestionnaire.residentCountriesSanctions'),
              value: getBooleanValue(residentCountriesSanctions),
            },
            {
              title: t('pages.dashboard.createUser.form.sanctionEmbargoQuestionnaire.involvedSanctionsTitle'),
              tooltip: (
                <div className="typo-body-medium-paragraph">
                  <p>{t('pages.dashboard.createUser.form.sanctionEmbargoQuestionnaire.involvedSanctions')}</p>
                  <ul className="list-disc ml-4">
                    <li>{t('pages.dashboard.createUser.form.sanctionEmbargoQuestionnaire.involvedSanctionItem1')}</li>
                    <li>{t('pages.dashboard.createUser.form.sanctionEmbargoQuestionnaire.involvedSanctionItem2')}</li>
                  </ul>
                </div>
              ),
              value: getBooleanValue(involvedSanctions),
            },
          ]
        : []),
    ],
    [
      entitySanctionsQuestionnaire,
      getBooleanValue,
      activityOutsideEu,
      residentCountriesSanctions,
      involvedSanctions,
      economicSanctions,
      t,
    ],
  );

  return (
    <div className="flex flex-col gap-3">
      <h6 className="typo-subtitle-subtitle1 text-secondary">
        {t('pages.dashboard.createUser.form.entitySanctionsEmbargo')}
      </h6>
      <div>
        {rowData.map((row, index) => (
          <div key={`sanction-embargo-${index}`} className="typo-body-regular-paragraph flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-tertiary">{row.title}</span>
              <InfoTooltip tooltipInfo={row.tooltip} infoClassName="min-w-[220px]" />
            </div>
            <span className="text-secondary">{row.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
